import { Link } from "@tanstack/react-router";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import Icon from "../../lib/components/custom/Icon";
import LanguageToggle from "../../lib/components/custom/LanguageToggle";
import { Button } from "../../lib/components/ui/button";
import { Card } from "../../lib/components/ui/card";
import { useApi } from "../../lib/hooks/api/use-api";
import Layout from "../layout";
import AuthSection from "./AuthSection";

export default function Settings() {
	const { t } = useTranslation();
	const { auth } = useApi();
	const { data: session } = auth.useSession();
	const isLoggedIn = !!session?.user;

	return (
		<Layout>
			<div className="p-4 md:p-8 border rounded-large min-h-full bg-gradient-main">
				<div className="max-w-2xl mx-auto">
					{/* Header */}
					<motion.div
						initial={{ opacity: 0, y: -20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.25, ease: "easeOut" }}
						className="mb-8"
					>
						<h1 className="text-3xl font-manrope font-semibold">
							{t("settings.header")}
						</h1>
						<p className="text-muted-foreground mt-1">
							{t("settings.subheader")}
						</p>
					</motion.div>

					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.25, delay: 0.05, ease: "easeOut" }}
						className="space-y-6"
					>
						{/* Language Toggle */}
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.25, delay: 0.05, ease: "easeOut" }}
						>
							<Card className="p-6">
								<div className="flex flex-col space-y-1.5 mb-4">
									<h3 className="font-semibold leading-none tracking-tight">
										{t("settings.language.header")}
									</h3>
									<p className="text-sm text-muted-foreground">
										{t("settings.language.subheader")}
									</p>
								</div>
								<LanguageToggle />
							</Card>
						</motion.div>

						{/* Auth - Only show when logged in */}
						{isLoggedIn && (
							<motion.div
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.25, delay: 0.1, ease: "easeOut" }}
							>
								<AuthSection />
							</motion.div>
						)}

						{/* Legal Links */}
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{
								duration: 0.25,
								delay: isLoggedIn ? 0.15 : 0.1,
								ease: "easeOut",
							}}
						>
							<Card className="p-6">
								<h2 className="text-lg font-semibold mb-4">
									{t("support.legal.header")}
								</h2>
								<div className="flex flex-col sm:flex-row gap-4">
									<Button variant="outline" className="flex-1" asChild>
										<Link to="/legal/privacy">
											<Icon name="Shield" className="w-4 h-4 mr-2" />
											{t("support.legal.privacy_policy")}
										</Link>
									</Button>
									<Button variant="outline" className="flex-1" asChild>
										<Link to="/legal/terms">
											<Icon name="FileText" className="w-4 h-4 mr-2" />
											{t("support.legal.terms_conditions")}
										</Link>
									</Button>
								</div>
							</Card>
						</motion.div>
					</motion.div>
				</div>
			</div>
		</Layout>
	);
}
